/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Injectable, Optional} from '@angular/core';
import {LocalUserProfile} from '../../../components/session/local-user-profile';
import * as _ from 'lodash';
import {map, Observable} from 'rxjs';

import {transformJsonObjectProperty} from '../../../components/util/adn-http-resource-transforms';
import {ResourceHelper} from '../../../components/util/resource-helper';
import {AdnApiClientService} from '../../common/controller/adn-api-client.service';
import {
  SignalAdminResourceApiBase,
  SignalAdminResourceListParams,
  SignalAdminResourceModel,
  SignalAdminResourcePage
} from '../../common/controller/signal-admin-resource-api';

export interface MarketplaceProductModel extends SignalAdminResourceModel {
  [key: string]: any;
}

const jsonTransforms = transformJsonObjectProperty('data');

function transformMarketplaceProductRequest(product: MarketplaceProductModel): MarketplaceProductModel {
  const transformed = _.cloneDeep(product);
  ['minimumCpm', 'minimumCpc', 'minimumBudget', 'maximumBudget'].forEach((fieldName) => {
    ResourceHelper.checkMoney(transformed, [fieldName]);
  });
  return jsonTransforms.transformRequest(transformed);
}

@Injectable({providedIn: 'root'})
export class MarketplaceProductApiService extends SignalAdminResourceApiBase<MarketplaceProductModel> {
  constructor(client: AdnApiClientService, @Optional() localUserProfile?: LocalUserProfile) {
    super(client, 'axproduct', {}, () => localUserProfile?.isReadOnly() || false);
  }

  override get(id: string): Observable<MarketplaceProductModel> {
    return super.get(id).pipe(map((model) => jsonTransforms.transformResponse(model)));
  }

  override list(params: SignalAdminResourceListParams): Observable<SignalAdminResourcePage<MarketplaceProductModel>> {
    return super.list(params).pipe(map((page) => ({
      ...page,
      results: (page.results || []).map((model) => jsonTransforms.transformResponse(model))
    })));
  }

  listOptions(params: Record<string, any> = {}): Observable<SignalAdminResourcePage<MarketplaceProductModel>> {
    return this.client.request<SignalAdminResourcePage<MarketplaceProductModel>>({
      method: 'GET',
      url: this.resourceUri('axproduct'),
      params
    }).pipe(map((page) => ({
      ...page,
      results: (page.results || []).map((model) => jsonTransforms.transformResponse(model))
    })));
  }

  override save(model: MarketplaceProductModel): Observable<MarketplaceProductModel> {
    return super.save(transformMarketplaceProductRequest(model)).pipe(
      map((saved) => jsonTransforms.transformResponse(saved))
    );
  }
}
